import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, Card, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { tokens } from "../../theme";
import { pageContainerSx } from "../../components/ui";

// Static platform-wide Terms, unlike Privacy.jsx's per-company uploaded PDF: this is
// shown pre-auth (linked from Register), so there is nothing to fetch and no company
// to scope it to.
const SECTIONS = [
  {
    heading: "1. Acceptance of Terms",
    body: "By registering for or using the Botriq platform, you agree to be bound by these Terms and Conditions. If you do not agree, do not register for or use the platform.",
  },
  {
    heading: "2. Account Registration",
    body: "You must provide accurate, current, and complete information during registration and keep it up to date. Accounts are subject to admin approval before access is granted, and Botriq may reject or suspend a registration at its discretion.",
  },
  {
    heading: "3. Acceptable Use",
    body: "You agree to use the platform only for lawful purposes and in accordance with your organization's agreement with Botriq. You are responsible for all activity that occurs under your account and credentials.",
  },
  {
    heading: "4. Data and Privacy",
    body: "Use of the platform is also governed by the privacy policy published by your company's administrator. Botriq processes data submitted through the platform as necessary to provide the service.",
  },
  {
    heading: "5. Service Availability",
    body: "Botriq aims to keep the platform available and reliable but does not guarantee uninterrupted access, and is not liable for outages, data loss, or damages arising from use of the platform, to the extent permitted by law.",
  },
  {
    heading: "6. Changes to These Terms",
    body: "Botriq may update these Terms from time to time. Continued use of the platform after changes take effect constitutes acceptance of the revised Terms.",
  },
];

// `embedded` mirrors Privacy.jsx's convention: renders as plain content with
// no page chrome, for use inside a dialog or another page's layout instead of
// the standalone route.
const Terms = ({ embedded = false }) => {
  const navigate = useNavigate();

  // Register's "Terms and Conditions" link opens this route with
  // target="_blank" (a fresh tab, so an in-progress registration form is never
  // unmounted and loses nothing). `navigate(-1)` is a no-op there: a
  // script-opened tab's history stack starts and ends at this page, so there
  // is nothing behind it to go back to — that was the original bug.
  //
  // The fix is to close THIS tab instead, returning focus to whichever tab
  // opened it (Register, Login, etc.) exactly as the user left it. `window
  // .close()` only succeeds on a tab the current script opened (or the user
  // navigated to via a link with a `target`) and only while that remains the
  // only entry in its history — both hold here on first load. If neither is
  // true (a bookmarked/direct visit to /terms, or the user reloaded first),
  // the browser silently ignores the call, so `navigate(-1)` still runs as a
  // same-tab fallback rather than leaving the button inert.
  const handleBack = () => {
    window.close();
    navigate(-1);
  };

  return (
    <Box
      sx={{
        ...pageContainerSx({ embedded, prose: true }),
        // Terms is also reachable straight from the login flow, OUTSIDE
        // MainLayout — so on that route it has to paint its own canvas.
        minHeight: embedded ? "auto" : "100vh",
        background: embedded ? "transparent" : tokens.bg,
      }}
    >
      {!embedded && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          size="small"
          sx={{
            mb: 1.5,
            color: tokens.text2,
            "&:hover": { color: tokens.text, background: tokens.surface2 },
          }}
        >
          Back
        </Button>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            textAlign: "center",
            px: 3,
            pt: 4,
            pb: 3.5,
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: tokens.accentSoft,
              color: tokens.accent,
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography
            sx={{ fontSize: 24, fontWeight: 700, letterSpacing: "-.02em", color: tokens.text }}
          >
            Terms and Conditions
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: tokens.text2,
              lineHeight: 1.6,
              mt: 1,
              maxWidth: 520,
              mx: "auto",
            }}
          >
            The terms that govern registration and use of the Botriq platform.
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 4 }}>
          <Stack gap={0}>
            {SECTIONS.map((section, i) => (
              <Box
                key={section.heading}
                sx={{
                  py: 3,
                  borderBottom: i === SECTIONS.length - 1 ? "none" : `1px solid ${tokens.border}`,
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: tokens.text, mb: 1.25 }}>
                  {section.heading}
                </Typography>
                <Typography sx={{ fontSize: 13.5, lineHeight: 1.7, color: tokens.text2 }}>
                  {section.body}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Card>
    </Box>
  );
};

Terms.propTypes = {
  embedded: PropTypes.bool,
};

export default Terms;
